import { signOut as amplifySignOut, fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth'
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import type { UserInfo } from '../types/auth'

const ORIGINAL_PAGE_KEY = 'AUTH_REDIRECT_ORIGINAL_PAGE_DRIVER_PAYR'

interface CognitoContextType {
  isLoading: boolean
  cognitoUser?: UserInfo
  getUserAttributes: (userInfo?: UserInfo) => Partial<{
    name: string
    email: string
    picture: string
    sub: string
  }>
  signOut: () => Promise<void>
  userHasAnyGroup: (checkGroups: string[]) => boolean
  userHasGroups: (checkGroups: string[]) => boolean
}

const CognitoContext = createContext<CognitoContextType | undefined>(undefined)

export function CognitoProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true)
  const [cognitoUser, setCognitoUser] = useState<UserInfo | undefined>()

  const signOut = async () => {
    try {
      await amplifySignOut()
      window.location.pathname = '/'
      window.location.reload()
    } catch (error) {
      console.log('error signing out: ', error)
    }
  }

  const fetchCurrentUser = async () => {
    try {
      const user = await fetchAuthSession()
      const attributes = user?.tokens?.idToken?.payload
      if (!attributes) {
        throw new Error('No attributes found')
      }
      const groups = (attributes?.['cognito:groups'] as string[]) || []
      setCognitoUser({
        attributes: {
          name: attributes.name as string,
          email: attributes.email as string,
          picture: attributes.picture as string,
          sub: attributes.sub as string,
        },
        groups,
      })
      const originalPage = localStorage.getItem(ORIGINAL_PAGE_KEY)
      if (originalPage) {
        localStorage.removeItem(ORIGINAL_PAGE_KEY)
        window.location.href = originalPage
      }
    } catch (error) {
      console.log('error fetching user info: ', error)
      localStorage.setItem(ORIGINAL_PAGE_KEY, window.location.href)
      await signInWithRedirect(import.meta.env.PROD ? { provider: 'Google' } : undefined)
    } finally {
      setIsLoading(false)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: only run on mount
  useEffect(() => {
    fetchCurrentUser()
  }, [])

  const getUserAttributes = (userInfo?: UserInfo) => {
    if (userInfo?.attributes) {
      const { name, email, picture, sub } = userInfo.attributes
      return { name, email, picture, sub }
    }

    return {}
  }

  const userHasAnyGroup = (checkGroups: string[]) => {
    if (!cognitoUser?.groups) return false
    return cognitoUser.groups.some(role => checkGroups.includes(role))
  }
  const userHasGroups = (checkGroups: string[]) => {
    if (!cognitoUser?.groups) return false
    return checkGroups.every(value => cognitoUser?.groups?.includes(value) ?? false)
  }

  const value = {
    isLoading,
    cognitoUser,
    getUserAttributes,
    signOut,
    userHasAnyGroup,
    userHasGroups,
  }

  return <CognitoContext.Provider value={value}>{children}</CognitoContext.Provider>
}

export function useCognitoUser() {
  const context = useContext(CognitoContext)
  if (context === undefined) {
    throw new Error('useCognitoUser must be used within a CognitoProvider')
  }
  return context
}
